import { envIsTesting } from 'src/const/envCheck';
import { RETAIL_API_URL } from 'src/utils/envConfig';
import { LogAction, LoggingFacade, LogStatus } from '@/types/Logging';
import { fsPath } from './__path';
import { getWindowLogger } from './remoteLogger';

const logger: LoggingFacade = getWindowLogger({
  file: `${fsPath}/cache.ts`,
});

const CACHE_NAME = 'trihow-cms-cache';

const getCacheKey = (assetUrl: string) => {
  // For Google Storage URLs, strip the query parameters
  if (assetUrl.includes('storage.googleapis.com')) {
    const url = new URL(assetUrl);
    return url.origin + url.pathname;
  }
  return assetUrl;
};

// Get the cache instance
const getCache = async (): Promise<Cache | null> => {
  if (envIsTesting) {
    return null;
  }

  if (!('caches' in self)) {
    return Promise.reject(
      new Error('Cache API does not exist in this environment'),
    );
  }

  return caches.open(CACHE_NAME);
};

// Check if a content payload is cached
export const isContentPayloadCached = async (
  unitId: string,
): Promise<boolean> => {
  try {
    const cache = await getCache();
    if (!cache) return false;
    const response = await cache.match(
      `${RETAIL_API_URL}/players/contentPayload?unitId=${unitId}&version=2&shouldReturnScript=true`,
    );

    return !!response;
  } catch (error) {
    console.error('Error checking content payload cache:', error);
    return false;
  }
};

export const isAssetCached = async (assetUrl: string) => {
  const cacheKey = getCacheKey(assetUrl);
  if (envIsTesting) return false;
  const cache = await caches.open(CACHE_NAME);
  const isCached = Boolean(await cache.match(cacheKey));
  return isCached;
};

export const cacheAsset = async (assetUrl: string) => {
  if (envIsTesting) return;
  const cache = await caches.open(CACHE_NAME);
  const cacheKey = getCacheKey(assetUrl);

  const isCached = await isAssetCached(assetUrl);
  if (!isCached) {
    try {
      const response = await fetch(assetUrl, { mode: 'no-cors' });
      await cache.put(cacheKey, response);
      logger.info({
        action: LogAction.CREATE,
        status: LogStatus.SUCCEEDED,
        data: { cache_url: assetUrl, cache_data: response },
      });
    } catch (error) {
      logger.error({
        action: LogAction.CREATE,
        status: LogStatus.FAILED,
        data: { cache_url: assetUrl, cache_error: error },
      });
      console.error('Failed to load asset', assetUrl, error);
    }
  }

  return true;
};

export const logCacheFreeSpace = () => {
  if (navigator.storage && navigator.storage.estimate) {
    navigator.storage.estimate().then((estimate) => {
      if (!estimate.quota || !estimate.usage) {
        return;
      }

      const quotaInGB = (estimate.quota / 1024 ** 3).toFixed(2);
      const usageInGB = (estimate.usage / 1024 ** 3).toFixed(2);

      logger.info({
        action: LogAction.READ,
        status: LogStatus.SUCCEEDED,
        data: { cache_free: quotaInGB, cache_in_use: usageInGB },
      });
      console.debug(`Storage Quota: ${quotaInGB} GB, Used: ${usageInGB} GB`);
    });
  }
};
